import React, { useCallback, useEffect, useRef, useState } from 'react'
import { ModalSide } from '../../../molecules/ModalSide/ModalSide'
import EditText from '../../../blocks.simple/editText/editText'
import Icon from '../../../blocks.simple/icon/icon'
import { cn } from 'ethcss'
import { color } from '../../../theme/tokens/color'
import translate from '../../../core/translate'
import { OpenStreetMap } from '../../../features/map/molecules/OpenStreetMap/OpenStreetMap-view'
import {
    OpenStreetMapLocation,
    OpenStreetMapSelectedLocation,
} from '../../../features/map/molecules/OpenStreetMap/OpenStreetMap-types'
import { TMapPoint } from '../../../features/map/models/map'
import { IDeviceAdvertisementFormState, IDeviceCity, IDeviceModalContentProps } from '../externalAdvertisement.types'
import { SwitchButton } from '../../../atoms/SwitchButton'
import { Radio } from '../../../atoms/Radio'
import styles from './devicesModalContent.jcss'

interface NominatimAddress {
    city?: string
    town?: string
    village?: string
    municipality?: string
    hamlet?: string
    state?: string
    region?: string
    county?: string
}

interface NominatimReverseResponse {
    address?: NominatimAddress
}

interface NominatimSearchResponse extends NominatimReverseResponse {
    display_name?: string
    lat?: string
    lon?: string
}

interface TimezoneResponse {
    timeZone?: string
}

interface ResolvedAddress {
    address: string
    lat: number
    lng: number
    city: IDeviceCity
}

const getCityName = (address: NominatimAddress): string =>
    address.city || address.town || address.village || address.municipality || address.hamlet || ''

const getRegionName = (address: NominatimAddress): string => address.state || address.region || address.county || ''

const resolveTimezone = async (lat: number, lng: number): Promise<string> => {
    const timezoneUrl = new URL('https://timeapi.io/api/TimeZone/coordinate')
    timezoneUrl.search = new URLSearchParams({ latitude: String(lat), longitude: String(lng) }).toString()
    const response = await fetch(timezoneUrl.toString())

    if (!response.ok) return ''

    const { timeZone = '' } = (await response.json()) as TimezoneResponse

    return timeZone
}

const resolveCity = async (lat: number, lng: number): Promise<IDeviceCity | null> => {
    const reverseGeocodeUrl = new URL('https://nominatim.openstreetmap.org/reverse')
    reverseGeocodeUrl.search = new URLSearchParams({
        format: 'jsonv2',
        lat: String(lat),
        lon: String(lng),
        zoom: '10',
        addressdetails: '1',
        'accept-language': 'ru',
    }).toString()

    try {
        const [reverseGeocodeResponse, timezone] = await Promise.all([
            fetch(reverseGeocodeUrl.toString()),
            resolveTimezone(lat, lng),
        ])

        if (!reverseGeocodeResponse.ok) return null

        const { address = {} } = (await reverseGeocodeResponse.json()) as NominatimReverseResponse
        const name = getCityName(address)
        const region = getRegionName(address)

        if (!name || !region || !timezone) return null

        return { name, region, timezone }
    } catch (error) {
        return null
    }
}

const resolveAddress = async (addressQuery: string): Promise<ResolvedAddress | null> => {
    const searchUrl = new URL('https://nominatim.openstreetmap.org/search')
    searchUrl.search = new URLSearchParams({
        format: 'jsonv2',
        q: addressQuery,
        limit: '1',
        addressdetails: '1',
        'accept-language': 'ru',
    }).toString()

    try {
        const response = await fetch(searchUrl.toString())

        if (!response.ok) return null

        const [result] = (await response.json()) as NominatimSearchResponse[]

        if (!result) return null

        const lat = Number(result.lat)
        const lng = Number(result.lon)
        const address = result.address || {}
        const name = getCityName(address)
        const region = getRegionName(address)

        if (!Number.isFinite(lat) || !Number.isFinite(lng) || !name || !region) return null

        const timezone = await resolveTimezone(lat, lng)

        if (!timezone) return null

        return {
            address: result.display_name || addressQuery,
            lat,
            lng,
            city: { name, region, timezone },
        }
    } catch (error) {
        return null
    }
}

export const DeviceModalContent: React.FC<IDeviceModalContentProps> = ({
    onClose,
    onSave,
    selected,
    selectedTab,
    selectedItems = [],
}) => {
    const isAdvertisementDevice = selectedTab === 'advertisementDevices'
    const isMultipleSelection = selectedItems.length > 1
    const fileInputRef = useRef<HTMLInputElement>(null)
    const [advertisementStatus, setAdvertisementStatus] = useState<boolean>(
        Boolean(selected.doohDspSettings?.enabled) && !isMultipleSelection
    )
    const [form, setForm] = useState<IDeviceAdvertisementFormState>({
        name: (selected.doohDspSettings && selected.doohDspSettings.name) || selected.name,
        photo: null,
        address: selected.address || '',
        lat: selected.lat ?? null,
        lng: selected.lng ?? null,
        city: (selected.doohDspSettings && selected.doohDspSettings.city) ?? null,
        resolution: (selected.doohDspSettings && selected.doohDspSettings.techReqId) ?? '',
    })
    const [isMapMode, setIsMapMode] = useState(false)
    const [mapLocation, setMapLocation] = useState<OpenStreetMapLocation>({
        name: selected.address || '',
        lat: selected.lat ?? null,
        lng: selected.lng ?? null,
    })
    const [photoPreviewUrl, setPhotoPreviewUrl] = useState<string | null>(
        (selected.doohDspSettings && selected.doohDspSettings.imageUrl) || null
    )
    const [isResolvingCity, setIsResolvingCity] = useState(false)
    const [addressError, setAddressError] = useState<string | null>(null)
    const [isValidatingAddress, setIsValidatingAddress] = useState(false)

    useEffect(() => {
        return () => {
            if (photoPreviewUrl && photoPreviewUrl.startsWith('blob:')) {
                URL.revokeObjectURL(photoPreviewUrl)
            }
        }
    }, [photoPreviewUrl])

    const updateField = (
        field: keyof Pick<IDeviceAdvertisementFormState, 'name' | 'resolution'>,
        value: string
    ): void => {
        setForm((currentForm) => ({
            ...currentForm,
            [field]: value,
        }))
    }

    const updateAddress = (address: string): void => {
        setAddressError(null)
        setForm((currentForm) => ({
            ...currentForm,
            address,
            lat: null,
            lng: null,
            city: null,
        }))
    }

    const openMap = (): void => {
        setMapLocation({
            name: form.address,
            lat: form.lat,
            lng: form.lng,
        })
        setIsMapMode(true)
    }

    const returnToForm = (): void => {
        setIsMapMode(false)
    }

    const changeMapLocation = useCallback((location: OpenStreetMapSelectedLocation): void => {
        setMapLocation(location)
    }, [])

    const changeMapCoordinates = useCallback((point: TMapPoint): void => {
        setMapLocation((currentLocation) => ({
            ...currentLocation,
            lat: point.lat,
            lng: point.lng,
        }))
    }, [])

    const clearMapLocation = useCallback((): void => {
        setMapLocation({
            name: '',
            lat: null,
            lng: null,
        })
    }, [])

    const applyMapLocation = async (): Promise<void> => {
        if (!mapLocation.name || mapLocation.lat === null || mapLocation.lng === null) return

        setIsResolvingCity(true)
        const city = await resolveCity(mapLocation.lat, mapLocation.lng)

        setForm((currentForm) => ({
            ...currentForm,
            address: mapLocation.name || '',
            lat: mapLocation.lat,
            lng: mapLocation.lng,
            city,
        }))
        setAddressError(null)
        setIsResolvingCity(false)
        setIsMapMode(false)
    }

    const handlePhotoChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const photo = event.target.files && event.target.files[0]

        if (!photo) return

        setForm((currentForm) => ({
            ...currentForm,
            photo,
        }))
        setPhotoPreviewUrl(URL.createObjectURL(photo))
    }

    const openPhotoPicker = (): void => {
        if (fileInputRef.current) {
            fileInputRef.current.value = ''
            fileInputRef.current.click()
        }
    }

    const saveFormData = (formToSave: IDeviceAdvertisementFormState): void => {
        const formData = new FormData()
        const payload = {
            digitalSignageId: selected.type === 'digitalSignage' ? [selected.id] : [],
            groupId: selected.type === 'group' ? [selected.id] : [],
            name: formToSave.name,
            address: formToSave.address,
            techReqId: formToSave.resolution,
            deviceType: 'indoor-fullhd',
            lat: formToSave.lat,
            lng: formToSave.lng,
            city: formToSave.city,
        }

        if (selected.type === 'group') {
            delete payload.techReqId
            delete payload.deviceType
        }

        if (formToSave.photo) {
            formData.append('file', formToSave.photo)
        }

        formData.append('payload', JSON.stringify(payload))

        onSave(formData)
    }

    const handleSave = async (): Promise<void> => {
        if (isAdvertisementDevice) {
            const targetItems = isMultipleSelection ? selectedItems : [selected]

            onSave({
                status: advertisementStatus,
                digitalSignageId: targetItems.filter((item) => item.type === 'digitalSignage').map((item) => item.id),
                groupId: targetItems.filter((item) => item.type === 'group').map((item) => item.id),
            })
            return
        }

        if (form.lat !== null && form.lng !== null && form.city) {
            saveFormData(form)
            return
        }

        const address = form.address.trim()

        if (!address) {
            setAddressError(translate('fieldIsRequired'))
            return
        }

        setIsValidatingAddress(true)
        setAddressError(null)
        const resolvedAddress = await resolveAddress(address)
        setIsValidatingAddress(false)

        if (!resolvedAddress) {
            setAddressError(translate('geocoderRequestError'))
            return
        }

        const resolvedForm = { ...form, ...resolvedAddress }
        setForm(resolvedForm)
        saveFormData(resolvedForm)
    }

    return (
        <div>
            <ModalSide
                position={'right'}
                title={isMultipleSelection ? translate('bulkDeviceSelection') : selected.name}
                onClose={onClose}
                onReject={isMapMode ? returnToForm : onClose}
                onAccept={isMapMode ? applyMapLocation : handleSave}
                rejectText={isMapMode ? translate('returnToForm') : undefined}
                acceptText={isMapMode ? translate('apply') : undefined}
                acceptDisabled={
                    isValidatingAddress ||
                    (isMapMode &&
                        (isResolvingCity || !mapLocation.name || mapLocation.lat === null || mapLocation.lng === null))
                }
            >
                {isMapMode ? (
                    <div className={styles.mapContent}>
                        <OpenStreetMap
                            inline={true}
                            isOpen={true}
                            onClose={returnToForm}
                            location={mapLocation}
                            onChangeLocation={changeMapLocation}
                            onChangePlacemarkGeometry={changeMapCoordinates}
                            onClearLocation={clearMapLocation}
                            addressSearchEnabled={false}
                        />
                    </div>
                ) : (
                    <div className={styles.content}>
                        {isAdvertisementDevice ? (
                            <>
                                {isMultipleSelection ? (
                                    <div className={styles.bulkAdvertisementStatus}>
                                        <Radio
                                            selected={!advertisementStatus}
                                            onClick={() => setAdvertisementStatus(false)}
                                            label={translate('disableDevicesForExternalAdvertisement')}
                                            className={styles.bulkAdvertisementRadio}
                                        />
                                        <Radio
                                            selected={advertisementStatus}
                                            onClick={() => setAdvertisementStatus(true)}
                                            label={translate('enableDevicesForExternalAdvertisement')}
                                        />
                                    </div>
                                ) : (
                                    <div className={styles.advertisementStatus}>
                                        <SwitchButton
                                            checked={advertisementStatus}
                                            onChange={() => setAdvertisementStatus((status) => !status)}
                                        />
                                        <div className={styles.advertisementStatusContent}>
                                            <div className={styles.advertisementStatusLabel}>
                                                {translate('useDeviceForExternalAdvertisement')}
                                            </div>
                                            <div className={styles.advertisementStatusHint}>
                                                {translate('useDeviceForExternalAdvertisementHint')}
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {/*<button type="button" className={styles.advertisementRequirementsLink}>*/}
                                {/*    {translate('technicalRequirements')}*/}
                                {/*</button>*/}
                            </>
                        ) : (
                            <>
                                {selected.type !== 'group' && (
                                    <>
                                        <div className={styles.field}>
                                            <div className={styles.field}>
                                                <EditText
                                                    label={translate(
                                                        selected.type === 'group' ? 'groupName' : 'displayName'
                                                    )}
                                                    value={form.name}
                                                    onChange={(value: string) => updateField('name', value)}
                                                />
                                            </div>
                                            <div className={styles.fieldLabel}>{translate('deviceLocationPhoto')}</div>
                                            <button
                                                type="button"
                                                className={styles.photoButton}
                                                onClick={openPhotoPicker}
                                                aria-label={translate(photoPreviewUrl ? 'replacePhoto' : 'uploadPhoto')}
                                            >
                                                {photoPreviewUrl ? (
                                                    <img
                                                        className={styles.photoPreview}
                                                        src={photoPreviewUrl}
                                                        alt={translate('deviceLocation')}
                                                    />
                                                ) : (
                                                    <Icon type={'plus'} size={16} color={color.iconDarkHigh} />
                                                )}
                                            </button>
                                            <input
                                                ref={fileInputRef}
                                                className={styles.fileInput}
                                                type="file"
                                                accept="image/jpeg,image/png,image/heic,image/heif,image/webp"
                                                onChange={handlePhotoChange}
                                            />
                                            <button
                                                type="button"
                                                className={cn(styles.photoAction, {
                                                    [styles.photoActionEmpty]: !photoPreviewUrl,
                                                })}
                                                onClick={openPhotoPicker}
                                            >
                                                {translate(photoPreviewUrl ? 'replacePhoto' : 'uploadPhotoPrompt')}
                                            </button>
                                        </div>
                                        <div className={styles.requirements}>
                                            <Icon
                                                type={'info'}
                                                size={16}
                                                color={color.info}
                                                containerClass={styles.requirementsIcon}
                                            />
                                            <div>
                                                <div className={styles.requirementsTitle}>
                                                    {translate('photoRequirements')}
                                                </div>
                                                <ul className={styles.requirementsList}>
                                                    <li>{translate('deviceClearlyVisibleInPhoto')}</li>
                                                    <li>{translate('supportedPhotoFormats')}</li>
                                                    <li>{translate('photoResolutionRequirements')}</li>
                                                    <li>{translate('maxPhotoSize')}</li>
                                                </ul>
                                                <div className={styles.requirementsSubtitle}>
                                                    {translate('doNotUsePhotos')}
                                                </div>
                                                <ul className={styles.requirementsList}>
                                                    <li>{translate('blackAndWhitePhotos')}</li>
                                                    <li>{translate('poorQualityDevicePhoto')}</li>
                                                </ul>
                                            </div>
                                        </div>
                                    </>
                                )}
                                <div className={styles.field}>
                                    <EditText
                                        label={translate('address')}
                                        placeholder={translate('enterAddress')}
                                        value={form.address}
                                        onChange={(value: string) => updateAddress(value)}
                                        validationBorder={Boolean(addressError)}
                                        error={addressError || undefined}
                                    />
                                    <button
                                        type="button"
                                        className={cn(styles.linkButton, {
                                            [styles.linkButtonWithError]: Boolean(addressError),
                                        })}
                                        onClick={openMap}
                                    >
                                        {translate('specifyLocationOnMap')}
                                        <span className={styles.linkArrow}>›</span>
                                    </button>
                                </div>
                                <div className={styles.field}>
                                    <EditText
                                        label={translate('contentResolutionRequirements')}
                                        placeholder={'1920x1080'}
                                        value={form.resolution}
                                        onChange={(value: string) => updateField('resolution', value)}
                                    />
                                    {/*<button type="button" className={styles.linkButton}>*/}
                                    {/*    {translate('technicalRequirements')}*/}
                                    {/*</button>*/}
                                </div>

                                {selected.type === 'group' && (
                                    <div className={styles.hint}>{translate('extAdvMultipleSettingsApplyHint')}</div>
                                )}
                            </>
                        )}
                    </div>
                )}
            </ModalSide>
        </div>
    )
}
